import React from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  getMarkerDisplayImage,
  getMarkerTypeIcon,
  getMarkerTypeStyle,
  MARKER_TYPE_LABELS,
  partOfStoryLine,
} from '../../utils/memoryMarkers';
import { personHref } from '../../utils/prettyArchiveLinks';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getApiBase } from '../../utils/apiBase';

const BACKEND_URL = getApiBase().replace(/\/api$/, '');

function personLabel(p) {
  if (!p) return null;
  const gn = (p.given_names || '').trim();
  const sn = (p.surname || '').trim();
  return `${gn} ${sn}`.trim() || 'Person';
}

/**
 * Warm memory anchor card — profile, workspace, and search results.
 */
export function MemoryMarkerCard({
  marker,
  canEdit = false,
  onEdit,
  onDelete,
  anchorPerson = null,
  linkedStory = null,
  linkedPerson = null,
  variant = 'profile',
  className = '',
}) {
  const Icon = getMarkerTypeIcon(marker.marker_type);
  const style = getMarkerTypeStyle(marker.marker_type);
  const imageUrl = getMarkerDisplayImage(marker);
  const resolvedImage = imageUrl ? resolveAssetUrl(BACKEND_URL, imageUrl) : null;
  const personForLink = linkedPerson || anchorPerson;
  const isWorkspace = variant === 'workspace';

  return (
    <li
      className={`group rounded-xl border border-[hsl(38_14%_88%)]/80 bg-background/60 transition hover:border-[#8B7355]/30 hover:bg-[hsl(42_28%_100%)] ${className}`}
      data-testid={`memory-marker-${marker.id}`}
    >
      <div className={`flex gap-3 ${isWorkspace ? 'p-4' : 'p-3.5'}`}>
        {resolvedImage ? (
          <div
            className={`shrink-0 overflow-hidden rounded-lg border border-[hsl(38_14%_88%)]/80 bg-muted ${
              isWorkspace ? 'h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem]' : 'h-14 w-14 sm:h-16 sm:w-16'
            }`}
          >
            <img
              src={resolvedImage}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg ${style.iconBg} ${
              isWorkspace ? 'h-16 w-16' : 'h-12 w-12 sm:h-14 sm:w-14'
            }`}
          >
            <Icon className={`${isWorkspace ? 'h-5 w-5' : 'h-4 w-4'} ${style.iconText}`} aria-hidden />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              <Badge
                variant="secondary"
                className="mb-0.5 text-[10px] font-normal capitalize tracking-wide"
              >
                {MARKER_TYPE_LABELS[marker.marker_type] || marker.marker_type}
              </Badge>
              <p className={`font-serif leading-snug text-[#1c1917] ${isWorkspace ? 'text-base' : 'text-base sm:text-[1.05rem]'}`}>
                {marker.title}
              </p>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                {marker.approx_date || 'Date not recorded'}
                {marker.location_label ? ` · ${marker.location_label}` : ''}
              </p>
            </div>
            {canEdit && onEdit && onDelete ? (
              <div className="flex shrink-0 gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onEdit(marker)}
                  aria-label="Edit memory"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(marker)}
                  aria-label="Remove memory"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : null}
          </div>

          {marker.description ? (
            <p className={`mt-2 text-sm leading-relaxed text-[#5c534a] ${isWorkspace ? '' : 'line-clamp-2'}`}>
              {marker.description}
            </p>
          ) : null}

          {anchorPerson && partOfStoryLine(anchorPerson) && !isWorkspace ? (
            <p className="mt-2 text-xs text-[#7a7268]">{partOfStoryLine(anchorPerson)}</p>
          ) : null}

          {personForLink && isWorkspace ? (
            <Link
              to={personHref(personForLink)}
              className="mt-2 inline-block text-xs text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-2"
            >
              Part of {personLabel(personForLink)}&apos;s story
            </Link>
          ) : null}

          {!personForLink && marker.attachment?.mode === 'timeline_only' && isWorkspace ? (
            <p className="mt-2 text-xs text-muted-foreground">Archive timeline</p>
          ) : null}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {Array.isArray(marker.tags) &&
              marker.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-[10px] font-normal">
                  {tag}
                </Badge>
              ))}
            {linkedStory && anchorPerson ? (
              <Link
                to={`${personHref(anchorPerson)}?view=stories&story=${linkedStory.id}`}
                className="inline-flex items-center gap-1 text-xs text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-2"
              >
                <BookOpen className="h-3 w-3" aria-hidden />
                Open linked story
              </Link>
            ) : linkedStory ? (
              <span className="inline-flex items-center gap-1 text-xs text-[#5c4a32]">
                <BookOpen className="h-3 w-3" aria-hidden />
                Linked story
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

export default MemoryMarkerCard;
